<template>
  <PageHeader :title="pageTitle" icon="mdi-piggy-bank-outline">
    <template v-slot:buttons>
      <v-btn color="primary" small dense class="ma-1" v-if="showBackButton" @click="backToAgentSelection"><v-icon dense>mdi-arrow-left</v-icon> Back to Agents</v-btn>
      <v-progress-circular indeterminate color="primary" :size="20" :width="2" class="ma-1" v-if="balance === undefined && (!isManager || selectedAgent)" />
      <template v-else-if="isOwnCashbox && (!isManager || selectedAgent)">
        <v-btn color="primary" small dense class="ma-1" v-if="hasSafe" :to="{ name: 'UltimateTzSafebox' }"><v-icon dense>mdi-bank</v-icon> Safe Box</v-btn>
        <v-btn color="primary" small dense class="ma-1" v-else-if="grabSafeStatus !== 'pending'" @click="grabSafeDialog = true"><v-icon dense>mdi-bank</v-icon> Grab Safe Box</v-btn>
        <v-chip color="warning" small class="ma-1" v-else><v-icon dense left>mdi-clock-outline</v-icon> Awaiting Approval</v-chip>
      </template>
    </template>
    
    <!-- Show Agent Selection for Managers -->
    <AgentSelection v-if="isManager && !selectedAgent" @agentSelected="onAgentSelected" />

    <!-- Show Cashbox Details (for non-managers OR managers with selected agent) -->
    <v-container fluid v-if="!isManager || selectedAgent">

      <v-row>

        <v-col cols="6" class="text-left">
          <ServerSideResult :test="balance">
            <Widget title="Balance">
              <Currency :value="balance" cents />
            </Widget>
          </ServerSideResult>
        </v-col>

        <v-col cols="6" class="text-left">
          <Widget title="Safe Box Owner">
            <span v-if="hasSafe">You</span>
            <span v-else>{{safeboxOwnerName}}</span>
          </Widget>
        </v-col>

      </v-row>


      <v-row>

        <v-col cols="6">
          <Widget title="Send Money">
            <Capture @submit="toMove" v-model="transfer" :balance="balance" />
          </Widget>
        </v-col>

      </v-row>

      <v-row>
        <v-col cols="12" class="text-h5 text--primary text-left">
          Transaction History
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <ServerSideResult :test="transactions">
            <Widget title="Transaction History">
              <v-data-table
                :headers="headers"
                :items="transactions"
                :items-per-page="25"
                class="elevation-1"
              >
              
                <template v-slot:item.at="{ item }">
                  <Time v-if="item.at" :time="item.at" />
                </template>
              
                <template v-slot:item.amount="{ item }">
                  <Currency v-if="item.amount" :value="item.amount" cents />
                </template>
              
                <template v-slot:item.after="{ item }">
                  <Currency v-if="item.after" :value="item.after" cents />
                </template>

              </v-data-table>
            </Widget>
          </ServerSideResult>
        </v-col>
      </v-row>

    </v-container>

    <v-dialog v-model="grabSafeDialog" width="400">
      <v-card>
        <v-card-title class="text-h5 grey lighten-2">Grab Safe Box</v-card-title>
        <v-card-text class="pt-4">
          <v-textarea
            v-model="narration"
            label="Narration"
            outlined
            rows="3"
            autofocus
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="grabSafeDialog = false">Cancel</v-btn>
          <v-btn color="primary" text :disabled="!narration.trim()" @click="grabSafe">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </PageHeader>

</template>

<script>
import lookups from '@/mixins/lookups';

import PageHeader from "@/components/PageHeader";
import Time from "@/components/Time";
import Currency from "@/components/Currency";
import ServerSideResult from "@/components/ServerSideResult";
import Widget from "@/components/Widget";
import AgentSelection from "@/views/partners/ultimate_tz/cashbox/AgentSelection";

import Capture from "@/views/partners/ultimate_tz/cashbox/Capture";


export default {

  components: { PageHeader, Time, Currency, ServerSideResult, Widget, Capture, AgentSelection },

  data() {
    return {

      transactions: undefined,
      safeboxOwnerName: undefined,
      balance:  undefined,
      grabSafeStatus: 'none',
      grabSafeDialog: false,
      narration: '',

      transfer: {},

      selectedAgent: null,
      isManagerFlag: undefined, // Fetch dynamically
      currentUserId: null,

      headers: [
        {
          text: 'When?',
          align: 'start',
          sortable: true,
          value: 'at',
        },
        {
          text: 'What?',
          align: 'start',
          sortable: true,
          value: 'type',
        },
        {
          text: 'Counter Party',
          align: 'start',
          sortable: true,
          value: 'name',
        },
        {
          text: 'Amount',
          align: 'end',
          sortable: true,
          value: 'amount',
        },
        {
          text: 'Balance',
          align: 'end',
          sortable: true,
          value: 'after',
        },
      ],
    }
  },

  computed: {
    hasSafe() { return this.$store.getters.hasUltimateSafe; },
    isManager() {
      return this.isManagerFlag === true;
    },
    pageTitle() {
      if (this.isManager && this.selectedAgent) {
        return `Cashbox: ${this.selectedAgent.name}`;
      } else if (this.isManager) {
        return 'Select Loan Consultant';
      }
      return 'Your Cashbox';
    },
    showBackButton() {
      return this.isManager && this.selectedAgent;
    },
    isOwnCashbox() {
      return !this.selectedAgent || this.selectedAgent.id === this.currentUserId;
    }
  },

  watch: {
    // balance(to) {

    // }
  },

  methods: {
    onAgentSelected(agent) {
      this.selectedAgent = agent;
      this.loadCashboxData();
    },
    backToAgentSelection() {
      this.selectedAgent = null;
      this.transactions  = undefined;
      this.balance       = undefined;
      this.grabSafeStatus = 'none';
      this.loadCashboxData();
    },
    grabSafe() {
      this.grabSafeDialog = false;
      this.remoteLookup('ultimateTzGrabSafe', { description: this.narration })
        .then(() => {
          this.narration = '';
          this.loadCashboxData();
        })
        .catch(e => console.error(e));
    },
    setWalletInfo(wallet) {
      this.balance = wallet.balance;
      if (wallet.user_has_safe) {
        this.safeboxOwnerName = 'You';
        this.grabSafeStatus   = 'none';
        this.$store.commit('setUltimateSafe', true);
      } else {
        this.safeboxOwnerName = wallet.safe_owner_name || 'Nobody';
        this.grabSafeStatus   = wallet.grab_safe_pending ? 'pending' : 'none';
        this.$store.commit('setUltimateSafe', false);
      }
    },
    toMove() {
      let params = this.transfer || {};
      params['recipient_id'] = (this.transfer.recipient || {}).id;
      params['name'] = (this.transfer.recipient || {}).name;
      params['safe'] = 'safe';
      this.$router.push({ name: 'UltimateTzCashboxSend', query: params });
    },
    loadCashboxData() {
      const params = this.selectedAgent?.id != null ? { agentId: this.selectedAgent.id } : {};
      this.remoteLookup('ultimateTzWalletInfo', params).then( wallet => this.setWalletInfo(wallet) ).catch(e => console.log(e));
      this.remoteLookup('ultimateTzWalletTransactions', params).then( transactions => this.transactions = transactions ).catch(e => this.transactions = null);
    },
  },

  mounted() {
    this.$store.commit('setUltimateSafe', false);
    this.remoteLookup('getFieldAgents', {
      depot: true,
    }).then(agents => {
      // Find ourselves in the list to check if we're a manager
      this.currentUserId = this.$store.getters.settings.fieldAgentId;
      const currentUser = agents.find(agent => agent.id === this.currentUserId);

      this.isManagerFlag = currentUser ? currentUser.is_depot_manager : false;
      
      // Now load cashbox data if needed
      // Always load own wallet info (no agentId) so grab safe status is known
      this.loadCashboxData();
    }).catch(e => {
      console.error('Failed to fetch agents:', e);
      this.isManagerFlag = false;
      // Fallback: load own cashbox
      this.loadCashboxData();
    });
  },

  mixins: [lookups],

}
</script>
